/**
 * Admin dashboard — platform vitals at a glance.
 *
 * Two sections today (users + questions by category). Designed to grow:
 * the layout is a stack of cards so new metric sections can be dropped
 * in without disturbing the existing ones.
 */

import { api, ApiError } from '../api.js';
import { auth } from '../auth.js';
import { escape } from '../components/form.js';
import { toast } from '../components/toast.js';
import { navigate } from '../router.js';
import { formatKobo } from '../wallet.js';

export async function renderAdminDashboard(root) {
  const me = auth.getMe();
  if (!me?.is_admin) {
    root.innerHTML = `
      <div class="appbar"><div class="appbar-title">Dashboard (admin)</div></div>
      <div class="pad"><div class="card" style="color:var(--color-danger)">Admins only.</div></div>
    `;
    return;
  }

  root.innerHTML = `
    <div class="appbar">
      <a href="/" data-link class="appbar-back" aria-label="Back">‹</a>
      <div class="appbar-title">Admin dashboard</div>
      <div class="appbar-spacer"></div>
      <span class="status-pill bad">ADMIN</span>
    </div>
    <div class="pad">
      <a href="/admin/v2" data-link class="card"
         style="display:flex;align-items:center;gap:12px;padding:14px 16px;margin-bottom:12px;
                background:linear-gradient(135deg,#221C45,#5A3CE6);color:#fff;text-decoration:none;">
        <span style="font-size:18px;">✦</span>
        <div style="flex:1;">
          <div style="font-weight:700;font-size:14px;">Try the new admin dashboard</div>
          <div style="font-size:11.5px;color:rgba(255,255,255,.75);margin-top:2px;">
            Desktop-first, full analytics across every metric. Preview — the classic admin stays here.
          </div>
        </div>
        <span style="font-size:14px;">→</span>
      </a>
      <div id="dashboard-page">
        <div class="card muted">Loading metrics…</div>
      </div>
    </div>
  `;

  const page = document.getElementById('dashboard-page');
  let m;
  try {
    m = await api('/admin/metrics');
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      navigate('/login');
      return;
    }
    toast.fromError(err);
    page.innerHTML = `<div class="card" style="color:var(--color-danger)">${escape(err.message)}</div>`;
    return;
  }

  // Pending withdrawals + financial headline + recon + top community
  // suggestions + feature flags — fire-and-forget in parallel so a slow
  // auxiliary call doesn't block the rest of the dashboard.
  const [pendingWithdrawals, financial, recon, suggestions, featureFlags] = await Promise.all([
    api('/admin/withdrawals?limit=1&offset=0').then((wd) => wd.total).catch(() => null),
    api('/admin/metrics/financial').catch(() => null),
    api('/admin/metrics/reconciliation').catch(() => null),
    api('/admin/suggestions?limit=3&offset=0').catch(() => null),
    api('/admin/feature-flags').catch(() => null),
  ]);

  page.innerHTML = `
    <h3 class="sec">Operational metrics</h3>
    <div class="card" style="padding:14px;">
      ${renderReconStrip(recon)}
      <div class="grid cols-2" style="gap:10px;margin-top:10px;">
        ${miniStat('Platform revenue', financial ? formatKobo(financial.platform_revenue_kobo) : '—')}
        ${miniStat(
          'Net flow (30d)',
          financial ? formatKobo(financial.net_flow_kobo) : '—',
          financial && financial.net_flow_kobo < 0 ? 'danger' : null,
        )}
      </div>
      <div class="row" style="justify-content:space-between;align-items:center;gap:10px;margin-top:12px;padding-top:10px;border-top:1px solid #1a1432;">
        <div class="muted" style="font-size:11px;">
          Drill into deposits, withdrawals, payouts, daily flow…
        </div>
        <a href="/admin/metrics" data-link class="button-secondary button-inline">Open metrics →</a>
      </div>
    </div>

    ${renderHomeBannerToggle(featureFlags)}

    ${renderHomeHeroToggle(featureFlags)}

    <h3 class="sec">Wallet</h3>
    <div class="card" style="padding:14px;">
      <div class="row" style="justify-content:space-between;align-items:center;gap:10px;">
        <div>
          <div style="font-family:var(--font-display);font-size:22px;font-weight:700;color:#fff;">
            ${pendingWithdrawals === null ? '—' : Number(pendingWithdrawals).toLocaleString('en-NG')}
          </div>
          <div class="muted" style="font-size:11px;text-transform:uppercase;letter-spacing:0.06em;margin-top:2px;">
            Pending withdrawals
          </div>
        </div>
        <a href="/admin/withdrawals" data-link class="${pendingWithdrawals ? 'btn sm gold' : 'button-secondary button-inline'}">
          ${pendingWithdrawals ? 'Process payouts →' : 'Open withdrawals →'}
        </a>
      </div>
      <div class="row" style="justify-content:space-between;align-items:center;gap:10px;margin-top:10px;padding-top:10px;border-top:1px solid #1a1432;">
        <div>
          <div style="font-size:13px;color:#fff;">Re-query Paystack deposit</div>
          <div class="muted" style="font-size:11px;margin-top:2px;">
            Reconcile a deposit by reference when the webhook was missed.
          </div>
        </div>
        <a href="/admin/deposits/requery" data-link class="button-secondary button-inline">Open →</a>
      </div>
    </div>

    <h3 class="sec">Active users</h3>
    <div class="card" style="padding:14px;">
      <div class="grid cols-2" style="gap:10px;">
        ${stat('DAU (24h)', m.users.dau)}
        ${stat('MAU (30d)', m.users.mau)}
      </div>
      <div class="muted" style="font-size:11px;margin-top:8px;">
        Counts distinct users seen on any authenticated request.
      </div>
    </div>

    <h3 class="sec">Web Push</h3>
    <div class="card" style="padding:14px;">
      <div class="muted" style="font-size:12px;margin-bottom:10px;">
        Fire a test notification. Leave the email blank to ping your own devices;
        type a user's email to verify that user installed + subscribed.
      </div>
      <div class="row" style="gap:8px;flex-wrap:wrap;align-items:center;">
        <input type="email"
          id="push-test-email"
          placeholder="someone@example.com (optional)"
          autocomplete="off"
          style="flex:1;min-width:200px;" />
        <button type="button" class="btn sm" id="push-test-btn">Send test push</button>
      </div>
      <div class="muted" id="push-test-status" style="font-size:11px;margin-top:8px;"></div>
    </div>

    <h3 class="sec">Bulk power-up grant</h3>
    <div class="card" style="padding:14px;">
      <div class="muted" style="font-size:12px;margin-bottom:10px;line-height:1.45;">
        One-shot: hand <b style="color:#fff;">1 × 50/50 power-up</b> to every active user.
        Used for testing days — the whole player base wakes up with one to spend.
        <b style="color:var(--pink-t);">Not idempotent</b>: clicking twice stacks. Preview first.
      </div>
      <div class="row" style="gap:8px;flex-wrap:wrap;">
        <button type="button" class="button-secondary button-inline" id="bulk-grant-preview-btn">
          Preview (dry run)
        </button>
        <button type="button" class="btn sm"
                id="bulk-grant-fire-btn"
                style="background:rgba(233,75,162,0.22);border:1px solid rgba(233,75,162,0.55);color:#fff;">
          🎯 Grant 1 × 50/50 to all
        </button>
      </div>
      <div class="muted" id="bulk-grant-status" style="font-size:11.5px;margin-top:8px;line-height:1.45;"></div>
    </div>

    <h3 class="sec">Welcome-gift backfill</h3>
    <div class="card" style="padding:14px;">
      <div class="muted" style="font-size:12px;margin-bottom:10px;line-height:1.45;">
        Catch-up grant for the one-time <b style="color:#fff;">1 eraser + 1 50/50</b>
        welcome gift. Reaches active users created in the last
        <b style="color:#fff;">N hours</b> who don't yet have the gift on file.
        <b style="color:var(--ok, #6f6)">Idempotent</b> — clicking twice is safe;
        already-granted users are skipped on every run.
      </div>
      <div class="row" style="gap:8px;flex-wrap:wrap;align-items:center;">
        <label for="wg-since-hours" class="muted" style="font-size:11.5px;">
          Look back
        </label>
        <input type="number"
               id="wg-since-hours"
               value="24"
               min="1"
               max="720"
               style="width:70px;background:var(--surf-2, #1a1432);color:#fff;border:1px solid #2a1f4a;border-radius:6px;padding:4px 8px;font-size:12.5px;" />
        <span class="muted" style="font-size:11.5px;">hours</span>
        <div style="flex:1;"></div>
        <button type="button" class="button-secondary button-inline" id="wg-backfill-preview-btn">
          Preview (dry run)
        </button>
        <button type="button" class="btn sm"
                id="wg-backfill-fire-btn"
                style="background:rgba(108,206,109,0.22);border:1px solid rgba(108,206,109,0.55);color:#fff;">
          🎁 Grant welcome gift
        </button>
      </div>
      <div class="muted" id="wg-backfill-status" style="font-size:11.5px;margin-top:8px;line-height:1.45;"></div>
    </div>

    <h3 class="sec">Users</h3>
    <div class="card" style="padding:14px;">
      <div class="grid cols-3" style="gap:10px;">
        ${stat('Total', m.users.total)}
        ${stat('Verified', m.users.verified)}
        ${stat('Admins', m.users.admins)}
      </div>
      <div class="grid cols-2" style="gap:10px;margin-top:12px;">
        ${stat('New (last 7d)', m.users.new_last_7d)}
        ${stat('New (last 30d)', m.users.new_last_30d)}
      </div>
      <div style="margin-top:12px;">
        <a href="/admin/users" data-link class="button-secondary button-inline">Browse users →</a>
      </div>
    </div>

    <h3 class="sec">Questions by category</h3>
    <div class="card" style="padding:12px;">
      <div class="row" style="margin-bottom:8px;">
        <div class="muted" style="font-size:11px;text-transform:uppercase;letter-spacing:0.08em;">
          ${m.questions.published} published / ${m.questions.total} total
        </div>
      </div>
      ${renderCategoryTable(m.questions.by_category)}
    </div>

    <h3 class="sec">Topic suggestions</h3>
    <div class="card" style="padding:14px;">
      ${renderSuggestionsPreview(suggestions)}
      <div class="row" style="justify-content:flex-end;margin-top:10px;padding-top:10px;border-top:1px solid #1a1432;">
        <a href="/admin/suggestions" data-link class="button-secondary button-inline">Open suggestions →</a>
      </div>
    </div>
  `;

  const pushBtn = document.getElementById('push-test-btn');
  const pushEmailInput = document.getElementById('push-test-email');
  if (pushBtn) {
    pushBtn.addEventListener('click', async () => {
      pushBtn.disabled = true;
      const statusEl = document.getElementById('push-test-status');
      if (statusEl) statusEl.textContent = 'Sending…';
      const targetEmail = (pushEmailInput?.value || '').trim();
      const body = targetEmail ? { email: targetEmail } : {};
      try {
        const res = await api('/admin/push/test', { method: 'POST', body });
        if (statusEl) {
          const target = res.target_email ? ` to ${escape(res.target_email)}` : '';
          statusEl.textContent =
            res.sent > 0
              ? `Sent to ${res.sent} device${res.sent === 1 ? '' : 's'}${target}.`
              : res.skipped_pref
                ? `Skipped${target} — that user opted out of the reminders topic.`
                : `No subscribed devices${target}. The user needs to install the PWA + grant permission first.`;
        }
      } catch (err) {
        if (statusEl) {
          statusEl.textContent =
            err.code === 'user_not_found'
              ? 'No user with that email.'
              : err.message || 'Push test failed.';
        }
      } finally {
        pushBtn.disabled = false;
      }
    });
  }
  wireHomeBannerToggle();
  wireHomeHeroToggle();
  wireBulkGrantFiftyfifty();
  wireWelcomeGiftBackfill();
}

function wireBulkGrantFiftyfifty() {
  const previewBtn = document.getElementById('bulk-grant-preview-btn');
  const fireBtn = document.getElementById('bulk-grant-fire-btn');
  const statusEl = document.getElementById('bulk-grant-status');

  // Date-stamped reason so distinct fires are distinguishable in the
  // CloudWatch grant log. YYYY-MM-DD keeps the alphabet sort sensible.
  const todayKey = new Date().toISOString().slice(0, 10);
  const reason = `bulk_test_seed:${todayKey}`;

  const setStatus = (msg, danger = false) => {
    if (!statusEl) return;
    statusEl.textContent = msg;
    statusEl.style.color = danger ? 'var(--pink-t)' : '';
  };

  previewBtn?.addEventListener('click', async () => {
    previewBtn.disabled = true;
    setStatus('Previewing…');
    try {
      const res = await api('/admin/fiftyfifty/grant-all', {
        method: 'POST',
        body: { count: 1, reason, dry_run: true },
      });
      setStatus(
        `Dry run: would grant 1 × 50/50 to ${res.total_active_users} active user${res.total_active_users === 1 ? '' : 's'}. ` +
        `Reason "${res.reason}". Click "Grant" to commit.`,
      );
    } catch (err) {
      setStatus(err?.message || 'Preview failed.', true);
    } finally {
      previewBtn.disabled = false;
    }
  });

  fireBtn?.addEventListener('click', async () => {
    if (!confirm('Grant 1 × 50/50 power-up to EVERY active user?\n\nThis is NOT idempotent — clicking again will stack.')) {
      return;
    }
    fireBtn.disabled = true;
    previewBtn && (previewBtn.disabled = true);
    setStatus('Granting…');
    try {
      const res = await api('/admin/fiftyfifty/grant-all', {
        method: 'POST',
        body: { count: 1, reason, dry_run: false },
      });
      setStatus(
        `Granted 1 × 50/50 to ${res.granted_to} / ${res.total_active_users} active users. Reason "${res.reason}".`,
      );
    } catch (err) {
      setStatus(err?.message || 'Bulk grant failed.', true);
      fireBtn.disabled = false;
      previewBtn && (previewBtn.disabled = false);
    }
  });
}

function wireWelcomeGiftBackfill() {
  const previewBtn = document.getElementById('wg-backfill-preview-btn');
  const fireBtn = document.getElementById('wg-backfill-fire-btn');
  const statusEl = document.getElementById('wg-backfill-status');
  const sinceEl = document.getElementById('wg-since-hours');
  if (!previewBtn || !fireBtn) return;

  const setStatus = (msg, danger = false) => {
    if (!statusEl) return;
    statusEl.textContent = msg;
    statusEl.style.color = danger ? 'var(--pink-t)' : '';
  };

  const readHours = () => {
    const raw = Number(sinceEl?.value || 24);
    // Clamp to the backend's accepted range (1..720) so a typo doesn't
    // round-trip to a 400.
    const clamped = Math.max(1, Math.min(720, Math.floor(raw) || 24));
    if (sinceEl && String(clamped) !== sinceEl.value) sinceEl.value = String(clamped);
    return clamped;
  };

  const summarise = (res) => {
    const parts = [];
    if (res.dry_run) {
      parts.push(`Dry run: would grant to ${res.granted_to} user${res.granted_to === 1 ? '' : 's'}.`);
    } else {
      parts.push(`Granted to ${res.granted_to} user${res.granted_to === 1 ? '' : 's'}.`);
    }
    parts.push(
      `Window: last ${res.since_hours}h · candidates ${res.candidates} ·` +
        ` already granted ${res.skipped_already_granted}` +
        (res.skipped_failed ? ` · failed ${res.skipped_failed}` : ''),
    );
    return parts.join(' ');
  };

  previewBtn.addEventListener('click', async () => {
    const sinceHours = readHours();
    previewBtn.disabled = true;
    setStatus('Previewing…');
    try {
      const res = await api('/admin/powerups/welcome-gift-backfill', {
        method: 'POST',
        body: { since_hours: sinceHours, dry_run: true },
      });
      setStatus(summarise(res));
    } catch (err) {
      setStatus(err?.message || 'Preview failed.', true);
    } finally {
      previewBtn.disabled = false;
    }
  });

  fireBtn.addEventListener('click', async () => {
    const sinceHours = readHours();
    if (!confirm(
      `Grant the welcome gift (1 eraser + 1 50/50) to all active users from the last ${sinceHours}h ` +
      `who don't already have it?\n\nThis IS idempotent — safe to re-run.`,
    )) {
      return;
    }
    fireBtn.disabled = true;
    previewBtn.disabled = true;
    setStatus('Granting…');
    try {
      const res = await api('/admin/powerups/welcome-gift-backfill', {
        method: 'POST',
        body: { since_hours: sinceHours, dry_run: false },
      });
      setStatus(summarise(res));
    } catch (err) {
      setStatus(err?.message || 'Backfill failed.', true);
    } finally {
      fireBtn.disabled = false;
      previewBtn.disabled = false;
    }
  });
}

function renderHomeBannerToggle(featureFlags) {
  // Six-way picker: which promo strip plays at the top of the home
  // screen. Mutually exclusive — only one renders at a time.
  //   - eraser         → all banner flags off (default)
  //   - fiftyfifty     → home_banner_show_fiftyfifty=true, others off
  //   - buy_fiftyfifty → home_banner_show_buy_fiftyfifty=true, others off
  //   - shop           → home_banner_show_shop=true, others off
  //   - poll           → home_banner_show_poll=true, others off
  //   - anti_cheat     → home_banner_show_anti_cheat=true, others off
  // Backend precedence is poll > anti_cheat > shop > buy > refer > eraser
  // so a stale flag row never breaks the banner.
  const items = featureFlags?.items || [];
  const referOn = !!items.find((f) => f.key === 'home_banner_show_fiftyfifty')?.enabled;
  const buyOn = !!items.find((f) => f.key === 'home_banner_show_buy_fiftyfifty')?.enabled;
  const shopOn = !!items.find((f) => f.key === 'home_banner_show_shop')?.enabled;
  const pollOn = !!items.find((f) => f.key === 'home_banner_show_poll')?.enabled;
  const antiCheatOn = !!items.find((f) => f.key === 'home_banner_show_anti_cheat')?.enabled;
  let active;
  if (pollOn) active = 'poll';
  else if (antiCheatOn) active = 'anti_cheat';
  else if (shopOn) active = 'shop';
  else if (buyOn) active = 'buy_fiftyfifty';
  else if (referOn) active = 'fiftyfifty';
  else active = 'eraser';

  const btnClass = (key) => (active === key ? 'btn sm' : 'button-secondary button-inline');
  const btnStyle = (key, bg, border) => active === key
    ? `flex:1;min-width:140px;background:${bg};border:1px solid ${border};color:#fff;`
    : 'flex:1;min-width:140px;';

  const label = {
    eraser: 'Buy erasers',
    fiftyfifty: 'Refer & Earn (50/50)',
    buy_fiftyfifty: 'Buy 50/50s',
    shop: 'Shop (all power-ups)',
    poll: 'Poll (active poll)',
    anti_cheat: 'Anti-cheat policy',
  }[active];

  return `
    <h3 class="sec">Home banner</h3>
    <div class="card" style="padding:14px;">
      <div class="muted" style="font-size:12px;margin-bottom:10px;line-height:1.45;">
        Which promo strip plays at the top of the home screen for every user.
        Pick one — flips live, no redeploy needed. The Poll option falls back
        to the previous banner if no poll is currently active.
      </div>
      <div class="row" style="gap:8px;flex-wrap:wrap;">
        <button type="button"
                id="banner-mode-eraser"
                class="${active === 'eraser' ? 'btn sm gold' : 'button-secondary button-inline'}"
                data-mode="eraser"
                data-active="${active === 'eraser' ? 'true' : 'false'}"
                style="flex:1;min-width:120px;">
          ✏️ Eraser — Buy
        </button>
        <button type="button"
                id="banner-mode-fiftyfifty"
                class="${btnClass('fiftyfifty')}"
                data-mode="fiftyfifty"
                data-active="${active === 'fiftyfifty' ? 'true' : 'false'}"
                style="${btnStyle('fiftyfifty', 'rgba(233,75,162,0.22)', 'rgba(233,75,162,0.55)')}">
          🎯 50/50 — Refer & Earn
        </button>
        <button type="button"
                id="banner-mode-buy-fiftyfifty"
                class="${btnClass('buy_fiftyfifty')}"
                data-mode="buy_fiftyfifty"
                data-active="${active === 'buy_fiftyfifty' ? 'true' : 'false'}"
                style="${btnStyle('buy_fiftyfifty', 'rgba(127,180,255,0.22)', 'rgba(127,180,255,0.55)')}">
          🎯 50/50 — Buy
        </button>
        <button type="button"
                id="banner-mode-shop"
                class="${btnClass('shop')}"
                data-mode="shop"
                data-active="${active === 'shop' ? 'true' : 'false'}"
                style="${btnStyle('shop', 'rgba(127,180,255,0.22)', 'rgba(127,180,255,0.55)')}">
          🛒 Shop — All power-ups
        </button>
        <button type="button"
                id="banner-mode-poll"
                class="${btnClass('poll')}"
                data-mode="poll"
                data-active="${active === 'poll' ? 'true' : 'false'}"
                style="${btnStyle('poll', 'rgba(247,198,103,0.22)', 'rgba(247,198,103,0.55)')}">
          📊 Poll
        </button>
        <button type="button"
                id="banner-mode-anti-cheat"
                class="${btnClass('anti_cheat')}"
                data-mode="anti_cheat"
                data-active="${active === 'anti_cheat' ? 'true' : 'false'}"
                style="${btnStyle('anti_cheat', 'rgba(43,179,155,0.22)', 'rgba(43,179,155,0.55)')}">
          🛡️ Anti-cheat policy
        </button>
      </div>
      <div class="muted" id="banner-mode-status" style="font-size:11px;margin-top:8px;">
        Currently showing: <b style="color:#fff;">${label}</b>
      </div>
      <a href="/admin/polls" data-link class="button-secondary button-inline" style="margin-top:10px;display:inline-block;">
        Manage polls →
      </a>
    </div>
  `;
}

function wireHomeBannerToggle() {
  // Pick one of six modes. We always write ALL underlying banner flags
  // so the row state stays coherent — picking "shop" sets the shop flag
  // ON and the other four OFF in parallel.
  const setMode = async (mode, btn) => {
    btn.disabled = true;
    const statusEl = document.getElementById('banner-mode-status');
    if (statusEl) statusEl.textContent = 'Saving…';
    try {
      await Promise.all([
        api('/admin/feature-flags/home_banner_show_fiftyfifty', {
          method: 'PATCH',
          body: { enabled: mode === 'fiftyfifty' },
        }),
        api('/admin/feature-flags/home_banner_show_buy_fiftyfifty', {
          method: 'PATCH',
          body: { enabled: mode === 'buy_fiftyfifty' },
        }),
        api('/admin/feature-flags/home_banner_show_shop', {
          method: 'PATCH',
          body: { enabled: mode === 'shop' },
        }),
        api('/admin/feature-flags/home_banner_show_poll', {
          method: 'PATCH',
          body: { enabled: mode === 'poll' },
        }),
        api('/admin/feature-flags/home_banner_show_anti_cheat', {
          method: 'PATCH',
          body: { enabled: mode === 'anti_cheat' },
        }),
      ]);
      renderAdminDashboard(document.getElementById('route-shell'));
    } catch (err) {
      if (statusEl) {
        statusEl.textContent = err?.message || 'Could not update the banner.';
      }
      btn.disabled = false;
    }
  };
  [
    'banner-mode-eraser',
    'banner-mode-fiftyfifty',
    'banner-mode-buy-fiftyfifty',
    'banner-mode-shop',
    'banner-mode-poll',
    'banner-mode-anti-cheat',
  ].forEach((id) => {
    document.getElementById(id)?.addEventListener('click', (e) => {
      if (e.currentTarget.dataset.active === 'true') return; // already on
      void setMode(e.currentTarget.dataset.mode, e.currentTarget);
    });
  });
}

function renderHomeHeroToggle(featureFlags) {
  // Two-button picker: which jumbotron renders in the Quick-match slot
  // on /home. The flag `home_hero_show_practice` is the source of truth
  // — true means the Solo-practice hero (default), false means the
  // 1-on-1 duel hero. Mirrors the home-banner toggle right above it.
  const items = featureFlags?.items || [];
  const practiceOn = !!items.find(
    (f) => f.key === 'home_hero_show_practice',
  )?.enabled;
  const duelBtnClass = practiceOn ? 'button-secondary button-inline' : 'btn sm gold';
  const practiceBtnClass = practiceOn ? 'btn sm gold' : 'button-secondary button-inline';
  return `
    <h3 class="sec">Home jumbotron</h3>
    <div class="card" style="padding:14px;">
      <div class="muted" style="font-size:12px;margin-bottom:10px;line-height:1.45;">
        Which hero sits above Game modes on the home screen. Flips live —
        no redeploy needed.
      </div>
      <div class="row" style="gap:8px;flex-wrap:wrap;">
        <button type="button"
                id="hero-mode-duel"
                class="${duelBtnClass}"
                data-active="${practiceOn ? 'false' : 'true'}"
                style="flex:1;min-width:140px;">
          ⚔️ Play 1-on-1
        </button>
        <button type="button"
                id="hero-mode-practice"
                class="${practiceBtnClass}"
                data-active="${practiceOn ? 'true' : 'false'}"
                style="flex:1;min-width:140px;">
          🧠 Solo practice
        </button>
      </div>
      <div class="muted" id="hero-mode-status" style="font-size:11px;margin-top:8px;">
        Currently showing: <b style="color:#fff;">${practiceOn ? 'Solo practice' : 'Play 1-on-1'}</b>
      </div>
    </div>
  `;
}

function wireHomeHeroToggle() {
  const setHero = async (enabled, btn) => {
    btn.disabled = true;
    const statusEl = document.getElementById('hero-mode-status');
    if (statusEl) statusEl.textContent = 'Saving…';
    try {
      await api('/admin/feature-flags/home_hero_show_practice', {
        method: 'PATCH',
        body: { enabled },
      });
      renderAdminDashboard(document.getElementById('route-shell'));
    } catch (err) {
      if (statusEl) {
        statusEl.textContent = err?.message || 'Could not update the hero.';
      }
      btn.disabled = false;
    }
  };
  document.getElementById('hero-mode-duel')?.addEventListener('click', (e) => {
    if (e.currentTarget.dataset.active === 'true') return;
    void setHero(false, e.currentTarget);
  });
  document.getElementById('hero-mode-practice')?.addEventListener('click', (e) => {
    if (e.currentTarget.dataset.active === 'true') return;
    void setHero(true, e.currentTarget);
  });
}

function stat(label, value) {
  return `
    <div style="text-align:center;padding:8px;background:var(--surf-2);border-radius:8px;">
      <div style="font-family:var(--font-display);font-size:22px;font-weight:700;color:#fff;">
        ${formatNumber(value)}
      </div>
      <div class="muted" style="font-size:11px;text-transform:uppercase;letter-spacing:0.06em;margin-top:2px;">
        ${escape(label)}
      </div>
    </div>
  `;
}

function miniStat(label, value, tone) {
  const fg = tone === 'danger' ? 'var(--color-danger)' : '#fff';
  const bg = tone === 'danger' ? 'var(--color-danger-soft)' : 'var(--surf-2)';
  return `
    <div style="text-align:center;padding:10px;background:${bg};border-radius:8px;">
      <div style="font-family:var(--font-display);font-size:18px;font-weight:700;color:${fg};">
        ${escape(String(value))}
      </div>
      <div class="muted" style="font-size:11px;text-transform:uppercase;letter-spacing:0.06em;margin-top:2px;">
        ${escape(label)}
      </div>
    </div>
  `;
}

function renderReconStrip(r) {
  // Three states: unknown (fetch failed), ok, drift. Default to a neutral
  // "checking..." pill so a transient fetch error doesn't shout STOP at the
  // admin — they'll see the muted state and know to refresh.
  if (!r) {
    return `
      <div style="padding:8px 10px;border-radius:8px;background:var(--surf-2);border-left:3px solid var(--color-text-muted);font-size:12.5px;">
        <span class="muted">Reconciliation unavailable — open Metrics for details.</span>
      </div>
    `;
  }
  if (r.ok) {
    return `
      <div style="padding:8px 10px;border-radius:8px;background:var(--color-success-soft);border-left:3px solid var(--color-success);font-size:12.5px;">
        <b style="color:var(--color-success);">Recon OK</b>
        <span class="muted" style="margin-left:6px;">${r.accounts_checked} accounts · ${r.txns_checked} txns</span>
      </div>
    `;
  }
  const parts = [];
  if (r.unbalanced_txns_count) parts.push(`${r.unbalanced_txns_count} unbalanced txn${r.unbalanced_txns_count === 1 ? '' : 's'}`);
  if (r.account_drift_count) parts.push(`${r.account_drift_count} account drift${r.account_drift_count === 1 ? '' : 's'}`);
  if (r.global_diff_kobo) parts.push(`Σ off by ${formatKobo(Math.abs(r.global_diff_kobo))}`);
  return `
    <div style="padding:8px 10px;border-radius:8px;background:var(--color-danger-soft);border-left:3px solid var(--color-danger);font-size:12.5px;">
      <b style="color:var(--color-danger);">RECON DRIFT</b>
      <span style="color:#fff;margin-left:6px;">${escape(parts.join(' · ') || 'see Metrics for details')}</span>
    </div>
  `;
}

function renderCategoryTable(rows) {
  if (!rows || rows.length === 0) {
    return `<div class="muted">No categories yet.</div>`;
  }
  // Compute a max so the bar widths are relative to the biggest one.
  const max = Math.max(1, ...rows.map((r) => r.total));
  return `
    <div style="display:grid;gap:7px;">
      ${rows.map((r) => {
        const pct = Math.round((r.total / max) * 100);
        return `
          <div>
            <div class="row" style="margin-bottom:3px;">
              <span style="font-size:13px;color:#fff;flex:1;">${escape(r.name)}</span>
              <span class="muted" style="font-size:11px;">${r.published} / ${r.total}</span>
            </div>
            <div style="height:6px;background:#1a1432;border-radius:3px;overflow:hidden;">
              <div style="height:100%;width:${pct}%;background:linear-gradient(90deg,#5B2C91,#E94BA2);"></div>
            </div>
          </div>
        `;
      }).join('')}
    </div>
  `;
}

function renderSuggestionsPreview(page) {
  if (!page) {
    return `<div class="muted">Couldn't load suggestions just now.</div>`;
  }
  if (page.total === 0) {
    return `<div class="muted">No suggestions yet — community members can submit topics with <code>/suggest</code> in the Telegram group.</div>`;
  }
  return `
    <div class="muted" style="font-size:11px;text-transform:uppercase;letter-spacing:0.08em;margin-bottom:8px;">
      ${page.total.toLocaleString('en-NG')} total · top by votes
    </div>
    <div style="display:grid;gap:6px;">
      ${page.items.map((row) => `
        <div class="row" style="gap:10px;align-items:baseline;">
          <span style="font-size:13.5px;color:#fff;flex:1;">${escape(row.display_text)}</span>
          <span style="font-family:var(--font-display);font-size:14px;font-weight:700;color:#fff;">${row.vote_count}</span>
          <span class="muted" style="font-size:11px;">votes</span>
        </div>
      `).join('')}
    </div>
  `;
}

function formatNumber(n) {
  if (n === null || n === undefined) return '—';
  return Number(n).toLocaleString('en-NG');
}
